import "./sidebar.css";
import { useState, useEffect } from "react";
import emcureLogo from "../../assets/images/emcure_logo.svg";
import iconAdd from "../../assets/images/icon_add.svg";
import iconSearch from "../../assets/images/icon_search.svg";
import iconDelete from "../../assets/images/icon_delete.svg";
import iconProfile from "../../assets/images/icon_profile.svg";
import { ToastContainer, toast } from "react-toastify";
import { getUserId, getUserName, getUserRole } from "../../utils/userUtils";
import sessionService from "../../services/sessionService";
import "react-toastify/dist/ReactToastify.css";

const Sidebar = ({ setNewSession, activeSessionId, setActiveSessionId, refreshSessions, isCollapsed }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState(null);

  const fetchSessions = async (isInitialLoad = false) => {
    setLoading(true);
    try {
      const userId = getUserId();

      const response = await sessionService.getUserSessions(userId);
      // Sort by last activity (updated_at or last message timestamp) to show most recent sessions first
      const sortedSessions = [...response.sessions].sort((a, b) => {
        // Use updated_at if available, otherwise fall back to created_at
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        return dateB - dateA; // Most recent first
      });
      const sessionList = sortedSessions.map((res) => ({
        id: res.session_id,
        sessionName: res.messages[0]?.content || "No messages",
        createdAt: res.created_at, // Store for reference
        updatedAt: res.updated_at, // Store for reference
      }));
      setSessions(sessionList);
      let selectedSessionId = activeSessionId;

      // Do not auto-select a session on the very first load so that
      // the chat opens on a clean landing / new-session view.
      if (!isInitialLoad && sessionList.length > 0 && !activeSessionId) {
        selectedSessionId = sessionList[0].id;
        setActiveSessionId(selectedSessionId);
      }

      // Dispatch event so ChatSection can update its sessions state
      window.dispatchEvent(new CustomEvent('sessionsUpdated', { detail: { sessions: sessionList } }));

      // On initial load, dispatch sidebarReady event so LandingPage can finish loading
      if (isInitialLoad) {
        window.dispatchEvent(new CustomEvent('sidebarReady', {
          detail: {
            activeSessionId: selectedSessionId,
            newSession: true
          }
        }));
      }
    } catch (error) {
      // On initial load, dispatch sidebarReady event even on error
      if (isInitialLoad) {
        window.dispatchEvent(new CustomEvent('sidebarReady', {
          detail: {
            activeSessionId: null,
            newSession: true
          }
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSessions(true); // Pass true for initial load

    const handleSessionDeleted = (event) => {
      const { hasRemainingSessions, firstSessionId } = event.detail || {};

      fetchSessions(false); // Pass false for non-initial loads

      if (hasRemainingSessions && firstSessionId) {
        setActiveSessionId(firstSessionId);
        setNewSession(false);
      } else {
        setActiveSessionId(null);
        setNewSession(true);
      }
    };

    const handleNewSessionAdded = (event) => {
      const { session, activeSessionId } = event.detail || {};
      if (session) {
        // Add the new session to the list optimistically without fetching
        // This prevents unnecessary API calls and maintains UI responsiveness
        setSessions(prev => {
          // Check if session already exists
          const exists = prev.some(s => s.id === session.id);
          if (exists) return prev;

          // Add new session at the beginning (newest first)
          return [session, ...prev];
        });

        // Set the new session as active
        if (activeSessionId) {
          setActiveSessionId(activeSessionId);
          setNewSession(false);
        }
      }
    };

    const handleSessionPersisted = (event) => {
      const { sessionId } = event.detail || {};
      if (sessionId) {
        // Refetch sessions from backend to get the persisted session with
        // proper metadata (created_at, first message, etc.)
        fetchSessions(false);
      }
    };

    const handleSessionActivity = (event) => {
      const { sessionId } = event.detail || {};
      if (sessionId) {
        // Move the active session to the top of the list (ChatGPT-like behavior)
        setSessions(prev => {
          const sessionIndex = prev.findIndex(s => s.id === sessionId);
          if (sessionIndex > 0) {
            // Session exists and is not already at the top
            const updatedSessions = [...prev];
            const [movedSession] = updatedSessions.splice(sessionIndex, 1);
            // Update the session's updatedAt to current time for visual feedback
            movedSession.updatedAt = new Date().toISOString();
            return [movedSession, ...updatedSessions];
          }
          return prev;
        });
      }
    };

    window.addEventListener('sessionDeleted', handleSessionDeleted);
    window.addEventListener('newSessionAdded', handleNewSessionAdded);
    window.addEventListener('sessionPersisted', handleSessionPersisted);
    window.addEventListener('sessionActivity', handleSessionActivity);

    return () => {
      window.removeEventListener('sessionDeleted', handleSessionDeleted);
      window.removeEventListener('newSessionAdded', handleNewSessionAdded);
      window.removeEventListener('sessionPersisted', handleSessionPersisted);
      window.removeEventListener('sessionActivity', handleSessionActivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: register event listeners once on mount
  }, []);

  useEffect(() => {
    if (refreshSessions > 0) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSessions stable, only refresh when refreshSessions changes
  }, [refreshSessions]);

  const handleSessionClick = (id) => {
    setActiveSessionId(id);
    setNewSession(false);
  };

  const confirmDelete = async () => {
    try {
      await sessionService.deleteSession(deleteSessionId);
      const updated = sessions.filter((s) => s.id !== deleteSessionId);
      setSessions(updated);

      if (activeSessionId === deleteSessionId) {
        if (updated.length > 0) {
          setActiveSessionId(updated[0].id);
          setNewSession(false);
        } else {
          setActiveSessionId(null);
          setNewSession(true);
        }
      }

      setShowModal(false);
      toast.success("Session deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete session.");
    }
  };

  const openDeleteModal = (id) => {
    setDeleteSessionId(id);
    setShowModal(true);
  };

  const filteredSessions = sessions.filter((session) =>
    session.sessionName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {!isCollapsed && (
        <>
          <div className="logo">
            <img src={emcureLogo} alt="Logo" />
          </div>

          <button onClick={() => { setNewSession(true); setActiveSessionId(null) }} className="new-session-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span className="new-session-text">New session</span>
          </button>

          <div className="search-input-div">
            <img src={iconSearch} alt="Search" />
            <input
              type="text"
              placeholder="Search"
              className="search-box"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="session-list">
            <p className="fS-12 color-939393 mB-4">Sessions</p>
            {loading ? (
              <p>Loading sessions...</p>
            ) : filteredSessions.length === 0 ? (
              <p>No sessions found</p>
            ) : (
              filteredSessions.map((session) => (
                <div
                  key={session.id}
                  className={`session-item ${activeSessionId === session.id ? "active" : ""
                    }`}
                  onClick={() => handleSessionClick(session.id)}
                >
                  <span>{session.sessionName}</span>
                  <img
                    src={iconDelete}
                    alt="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(session.id);
                    }}
                  />
                </div>
              ))
            )}
          </div>

          <div className="user-info">
            <div>
              <div className="user-name">{getUserName()}</div>
              <div className="user-role">{getUserRole()}</div>
            </div>
            <img src={iconProfile} alt="Profile" />
          </div>

          {showModal && (
            <div
              className="custom-modal"
              onClick={() => setShowModal(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                backdropFilter: "blur(2px)",
              }}
            >
              <div
                className="custom-modal-dialog"
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  maxWidth: "420px",
                  width: "90%",
                  overflow: "hidden",
                  animation: "modalFadeIn 0.2s ease-out",
                }}
              >
                <div className="custom-modal-content">
                  <div
                    className="custom-modal-header"
                    style={{
                      padding: "20px 24px",
                      borderBottom: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <h5
                      className="custom-modal-title"
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#111827",
                      }}
                    >
                      Confirm Delete
                    </h5>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowModal(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        color: "#6B7280",
                        cursor: "pointer",
                        padding: "0",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F3F4F6";
                        e.currentTarget.style.color = "#111827";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#6B7280";
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    className="custom-modal-body"
                    style={{
                      padding: "24px",
                      fontSize: "14px",
                      color: "#374151",
                      lineHeight: "1.6",
                    }}
                  >
                    Are you sure you want to delete this session? This action cannot be undone.
                  </div>
                  <div
                    className="custom-modal-footer"
                    style={{
                      padding: "16px 24px",
                      borderTop: "1px solid #E5E7EB",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowModal(false);
                      }}
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        backgroundColor: "#fff",
                        border: "1px solid #D1D5DB",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F9FAFB";
                        e.currentTarget.style.borderColor = "#9CA3AF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                        e.currentTarget.style.borderColor = "#D1D5DB";
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        confirmDelete();
                      }}
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#fff",
                        backgroundColor: "#B90005",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#B91C1C";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#B90005";
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ToastContainer />
        </>
      )}
    </div>
  );
};

export default Sidebar;
